import { ThunkAction } from "redux-thunk";
import { Action } from "redux";
import {
  TemplateActionTypes,
  TemplateSummaryDto,
  TemplateDetailDto,
} from "./types";
import { ApplicationState } from "../index";
import config from "../../config";

const BASE = config.api.DTS_API_CAMPAIGN; // or whatever base

const makeHeaders = (token: string | null | undefined) => ({
  "Content-Type": "application/json",
  ...(token ? { Authorization: `Bearer ${token}` } : {}),
});

export type AppThunk = ThunkAction<void, ApplicationState, null, Action>;

// ─── LIST ─────────────────────────────────────────────────────
export const fetchTemplates = (): AppThunk => async (dispatch, getState) => {
  const { auth: { token } } = getState();
  dispatch({ type: TemplateActionTypes.GET_TEMPLATES_REQUEST });
  try {
    const res = await fetch(`${BASE}/api/v1/templates`, {
      headers: makeHeaders(token),
    });
    const body = await res.json();
    if (!res.ok) throw new Error(body.message || res.statusText);
    dispatch({
      type: TemplateActionTypes.GET_TEMPLATES_SUCCESS,
      payload: body as TemplateSummaryDto[],
    });
  } catch (err: any) {
    dispatch({
      type: TemplateActionTypes.GET_TEMPLATES_ERROR,
      payload: err.message,
    });
  }
};

// ─── GET SINGLE ────────────────────────────────────────────────
export const fetchTemplateById = (id: string): AppThunk => async (
  dispatch,
  getState
) => {
  const { auth: { token } } = getState();
  dispatch({ type: TemplateActionTypes.GET_TEMPLATE_REQUEST });
  try {
    const res = await fetch(`${BASE}/api/v1/templates/${id}`, {
      headers: makeHeaders(token),
    });
    const body = await res.json();
    if (!res.ok) throw new Error(body.message || res.statusText);
    dispatch({
      type: TemplateActionTypes.GET_TEMPLATE_SUCCESS,
      payload: body as TemplateDetailDto,
    });
  } catch (err: any) {
    dispatch({
      type: TemplateActionTypes.GET_TEMPLATE_ERROR,
      payload: err.message,
    });
  }
};

// ─── CREATE ────────────────────────────────────────────────────
export const createTemplate = (
  data: Partial<TemplateDetailDto>
): AppThunk => async (dispatch, getState) => {
  const { auth: { token } } = getState();
  dispatch({ type: TemplateActionTypes.CREATE_TEMPLATE_REQUEST });
  try {
    const res = await fetch(`${BASE}/api/v1/templates`, {
      method: "POST",
      headers: makeHeaders(token),
      body: JSON.stringify(data),
    });
    const body = await res.json();
    if (!res.ok) throw new Error(body.message || res.statusText);
    dispatch({
      type: TemplateActionTypes.CREATE_TEMPLATE_SUCCESS,
      payload: body as { id: string },
    });
    // re-fetch list
    dispatch(fetchTemplates());
  } catch (err: any) {
    dispatch({
      type: TemplateActionTypes.CREATE_TEMPLATE_ERROR,
      payload: err.message,
    });
  }
};

// ─── UPDATE ────────────────────────────────────────────────────
export const updateTemplate = (
  id: string,
  data: Partial<TemplateDetailDto>
): AppThunk => async (dispatch, getState) => {
  const { auth: { token } } = getState();
  dispatch({ type: TemplateActionTypes.UPDATE_TEMPLATE_REQUEST });
  try {
    const res = await fetch(`${BASE}/api/v1/templates/${id}`, {
      method: "PUT",
      headers: makeHeaders(token),
      body: JSON.stringify(data),
    });
    const body = await res.json();
    if (!res.ok) throw new Error(body.message || res.statusText);
    dispatch({
      type: TemplateActionTypes.UPDATE_TEMPLATE_SUCCESS,
      payload: body as TemplateDetailDto,
    });
    // re-fetch list
    dispatch(fetchTemplates());
  } catch (err: any) {
    dispatch({
      type: TemplateActionTypes.UPDATE_TEMPLATE_ERROR,
      payload: err.message,
    });
  }
};

// ─── DELETE ────────────────────────────────────────────────────
export const deleteTemplate = (id: string): AppThunk => async (
  dispatch,
  getState
) => {
  const { auth: { token } } = getState();
  dispatch({ type: TemplateActionTypes.DELETE_TEMPLATE_REQUEST });
  try {
    const res = await fetch(`${BASE}/api/v1/templates/${id}`, {
      method: "DELETE",
      headers: makeHeaders(token),
    });
    if (!res.ok) {
      const body = await res.json();
      throw new Error(body.message || res.statusText);
    }
    dispatch({
      type: TemplateActionTypes.DELETE_TEMPLATE_SUCCESS,
      payload: id,
    });
    // re-fetch list
    dispatch(fetchTemplates());
  } catch (err: any) {
    dispatch({
      type: TemplateActionTypes.DELETE_TEMPLATE_ERROR,
      payload: err.message,
    });
  }
};
